<script setup lang="ts">
import { computed, markRaw, ref, watch } from 'vue';
import { useMediaQuery } from '@vueuse/core';
import DataTable from '@/components/DataTable/DataTable.vue';
import { isPlainClick } from '@/components/DataTable/rowClick';
import AudioFileIssuesTableHeader from '@/components/DataTable/AudioFileIssuesTableHeader.vue';
import ImgPreview from '@/components/ImgPreview/ImgPreview.vue';
import AudioFileIssueInspector from '@/components/AudioFileIssueInspector.vue';
import AudioFileIssueCard from '@/components/AudioFileIssues/AudioFileIssueCard.vue';
import AudioFileIssueCardSkeleton from '@/components/AudioFileIssues/AudioFileIssueCardSkeleton.vue';
import AudioFileIssueSplitView from '@/components/AudioFileIssues/AudioFileIssueSplitView.vue';
import SeverityBadge from '@/components/AudioFileIssues/SeverityBadge.vue';
import TrackRatingBadge from '@/components/AudioFileIssues/TrackRatingBadge.vue';
import { Tooltip, TooltipContent, TooltipRow, TooltipTrigger } from '@/components/ui/tooltip';
import { BadgeCheck, CalendarPlus, Upload } from 'lucide-vue-next';
import {
  useAudioFileIssueTypes,
  useAudioFileIssuesSearch,
  type AudioFileIssueRow,
} from '@/composables/api/useAudioFileIssuesApi';
import { useInspectorStore } from '@/stores/inspectorStore';
import { useInspectorTopOffset } from '@/composables/useInspectorTopOffset';
import { usePersistentTable } from '@/composables/usePersistentTable';
import { useRoutedSavedView } from '@/composables/useRoutedSavedView';
import { useRouter } from 'vue-router';
import type { TableColumn } from '@/types/tableTypes';
import { formatUsMmDdYyTime12h } from '@/utils/date';
import { UNKNOWN_ALBUM_ART_PATH } from '@/constants/coverArt';

// Keep the side-peek inspector below the toolbar row so the filters and
// paginator stay visible and clickable while it is open.
const toolbarRef = ref<HTMLElement | null>(null);
useInspectorTopOffset(toolbarRef);

const {
  pinnedColumns,
  unpinnedColumns,
  columnSort,
  compositeFilter,
  pageIndex,
  pageSize,
  toggleSort,
  setColumnSize,
  updateColumnState,
  tableLayout,
} = usePersistentTable();

// This page's saved views live in the section sidebar. Opening one puts it in
// the address (`?view=<id>`), as on a list.
useRoutedSavedView();

const listQuery = useAudioFileIssuesSearch({
  pageIndex,
  pageSize,
  sorting: columnSort,
  filter: compositeFilter,
});
const issueTypesQuery = useAudioFileIssueTypes();

const listData = computed(() => listQuery.data.value);
const issueTypeOptions = computed(() => issueTypesQuery.data.value?.data ?? []);

/** Slug → human label map so the multi-issue tooltip can list issue types by name. */
const issueTypeLabelMap = computed<Record<string, string>>(() => {
  const labelMap: Record<string, string> = {};
  for (const option of issueTypeOptions.value) {
    labelMap[option.value] = option.label;
  }
  return labelMap;
});

function otherIssueTypeLabels(row: AudioFileIssueRow): string[] {
  if (!row.issue_types || row.issue_types.length <= 1) {
    return [];
  }
  return row.issue_types
    .filter((issueType) => issueType !== row.issue_type)
    .map((issueType) => issueTypeLabelMap.value[issueType] ?? issueType);
}

const inspectorStore = useInspectorStore();
const router = useRouter();

/**
 * Desktop split-view mode (SAU-164) is only active when the user is in card
 * layout AND the viewport is wide enough to render both panes without crowding.
 * The breakpoint matches Tailwind's `md` (768px) so it lines up with the rest
 * of the page's responsive rules.
 */
const isDesktop = useMediaQuery('(min-width: 768px)');
const isSplitViewActive = computed(() => tableLayout.value === 'cards' && isDesktop.value);
const selectedTrackCode = ref<string | null>(null);

/** Drop the selection when the visible rows change so we never highlight a row that's no longer rendered. */
watch(
  () => listData.value?.data ?? [],
  (rows) => {
    if (selectedTrackCode.value === null) return;
    const stillVisible = rows.some((row) => row.live_track_code === selectedTrackCode.value);
    if (!stillVisible) {
      selectedTrackCode.value = null;
    }
  },
);

/**
 * Card/split-view uses its own inline detail pane, so the overlay inspector is
 * never opened here. Close any inspector that's still open when split-view
 * becomes active — e.g. one carried over from another page (the Playlists
 * report side-peek) — so the two detail surfaces don't show at once. Leaving
 * split-view just drops the inline selection. `immediate` covers landing on the
 * page already in card layout with a stale inspector open.
 */
watch(
  isSplitViewActive,
  (active) => {
    if (active) {
      if (inspectorStore.isOpen) {
        inspectorStore.close();
      }
    } else {
      selectedTrackCode.value = null;
    }
  },
  { immediate: true }
);

/**
 * The track whose repair panel is open in the peek view, so the table can highlight its row.
 * It is separate from `selectedTrackCode`, which belongs to the split view's inline pane.
 * It stays set while the person drills further into the peek view, and clears when it closes.
 */
const openTableTrackCode = ref<string | null>(null);

watch(
  () => inspectorStore.isOpen,
  (open) => {
    if (!open) {
      openTableTrackCode.value = null;
    }
  },
);

function openStandaloneInspectorInNewTab(trackCode: string) {
  const href = router.resolve({
    name: 'audio-file-issues-detail',
    params: { code: trackCode },
  }).href;
  window.open(href, '_blank', 'noopener,noreferrer');
}

function onIssueRowClick(row: AudioFileIssueRow, event?: MouseEvent) {
  const openInBackgroundTab = Boolean(event?.metaKey || event?.ctrlKey);
  if (openInBackgroundTab && event) {
    event.preventDefault();
    openStandaloneInspectorInNewTab(row.live_track_code);
    return;
  }
  if (isSplitViewActive.value) {
    selectedTrackCode.value = row.live_track_code;
    return;
  }
  openInspector(row);
}

/**
 * A click on a table row.
 * Cmd-click and ctrl-click open the repair panel in a new tab, through `onIssueRowClick`.
 * A shift-click or an alt-click does nothing, because this table has no checkboxes to tick.
 * The card layouts call `onIssueRowClick` directly, so a shift-click on a card opens the panel like a plain click.
 */
function onIssueTableRowClick(row: AudioFileIssueRow, event: MouseEvent) {
  const opensNewTab = event.metaKey || event.ctrlKey;
  if (!opensNewTab && !isPlainClick(event)) return;
  onIssueRowClick(row, event);
}

function openInspector(row: AudioFileIssueRow) {
  openTableTrackCode.value = row.live_track_code;
  const subtitleParts = [row.live_track_artist_name, row.live_track_code].filter(
    (p): p is string => Boolean(p)
  );

  inspectorStore.open(
    row.live_track_name ?? row.live_track_code,
    markRaw(AudioFileIssueInspector),
    { trackCode: row.live_track_code },
    {
      subtitle: subtitleParts.length > 0 ? subtitleParts.join(' · ') : undefined,
      route: {
        name: 'audio-file-issues-detail',
        params: { code: row.live_track_code },
      },
    }
  );

}

function detailsPreview(details: Record<string, unknown> | null): string {
  if (!details) {
    return '';
  }
  const entries = Object.entries(details).slice(0, 3);
  return entries.map(([k, v]) => `${k}=${formatScalar(v)}`).join(' • ');
}

function formatScalar(value: unknown): string {
  if (value === null || value === undefined) {
    return '—';
  }
  if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
    return String(value);
  }
  if (Array.isArray(value)) {
    return `[${value.length}]`;
  }
  return '{...}';
}

function cellDateDisplay(value: string | null | undefined): string {
  const out = formatUsMmDdYyTime12h(value);

  return out || '—';
}

const totalItems = computed(() => listData.value?.meta?.total ?? 0);

const columnsResizeHandler = (columnId: string, width: number) => {
  setColumnSize(columnId, width);
};

const columnsSortHandler = (columnField: string) => {
  toggleSort(columnField);
};

const columnsReorderHandler = (columns: TableColumn[]) => {
  updateColumnState(columns);
};

/** Bridge the loose Record<string, unknown> from DataTable's `data` prop to the typed row. */
function asRow(record: Record<string, unknown>): AudioFileIssueRow {
  return record as unknown as AudioFileIssueRow;
}
</script>

<template>
  <div class="flex flex-1 flex-col min-h-0">
    <div
      ref="toolbarRef"
      class="shrink-0 bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/95 z-30"
    >
      <AudioFileIssuesTableHeader :total-records="totalItems" />
    </div>

    <div
      v-if="listQuery.error?.value"
      class="border-destructive/50 bg-destructive/10 text-destructive shrink-0 mx-4 mb-2 rounded-md border p-3 text-sm"
    >
      <span>Could not load issues list.</span>
      <span class="text-muted-foreground"> Try refreshing; if it persists, contact support.</span>
    </div>

    <div
      class="min-h-0 flex-1 w-full"
      :class="isSplitViewActive ? 'overflow-hidden px-4 pb-4' : 'overflow-auto px-4 pb-4 max-md:pb-mobile-area-bar-clearance'"
    >
      <!--
        Desktop card mode: split-view (SAU-164). The card list lives in the
        left pane, the AudioFileIssueInspector is rendered inline in the right
        pane against the selected track. No overlay inspector opens here.
      -->
      <AudioFileIssueSplitView
        v-if="isSplitViewActive"
        :rows="listData?.data ?? []"
        :selected-track-code="selectedTrackCode"
        :other-issue-type-labels-for="otherIssueTypeLabels"
        :is-loading="listQuery.isLoading.value"
        @select="onIssueRowClick"
      />
      <!--
        Mobile card mode: a vertical list of inspector-style preview cards.
        Same rows array as the table, same click handler, so filters / sort /
        pagination / cmd-click-new-tab all keep working unchanged. Clicking a
        card still opens the modal inspector — there isn't enough room for
        side-by-side on phones.
      -->
      <div
        v-else-if="tableLayout === 'cards'"
        data-testid="audio-file-issues-card-list"
        class="flex w-full max-w-3xl flex-col gap-3 pt-2"
      >
        <template v-if="listQuery.isLoading.value && (listData?.data?.length ?? 0) === 0">
          <AudioFileIssueCardSkeleton v-for="i in 8" :key="`skeleton-${i}`" />
        </template>
        <AudioFileIssueCard
          v-for="row in listData?.data ?? []"
          :key="`${row.live_track_code}-${row.id}`"
          :row="row"
          :other-issue-type-labels="otherIssueTypeLabels(row)"
          @click="onIssueRowClick"
        />
        <div
          v-if="(listData?.data?.length ?? 0) === 0 && !listQuery.isLoading.value"
          class="text-muted-foreground py-8 text-center text-sm"
        >
          No audio file issues match the current filters.
        </div>
      </div>
      <DataTable
        v-else
        data-testid="audio-file-issues-table"
        table-in-parent-scroll
        :data="(listData?.data as unknown as Record<string, unknown>[])"
        :total-records="totalItems"
        :columns="unpinnedColumns"
        :pinned-columns="pinnedColumns"
        :is-loading="listQuery.isLoading.value"
        :page-index="pageIndex"
        :page-size="pageSize"
        row-id-key="live_track_code"
        :selected-row-id="openTableTrackCode"
        sticky-header
        @columnsResized="columnsResizeHandler"
        @columnsSorted="columnsSortHandler"
        @columnsReordered="columnsReorderHandler"
        @row-click="(row, event) => onIssueTableRowClick(asRow(row), event)"
      >
        <template #cell="{ column, row }">
          <div
            v-if="column.id === 'cover_art'"
            class="flex items-center justify-center"
            data-row-click-ignore
            @click.stop
          >
            <ImgPreview
              :src="asRow(row).cover_art_signed_url ?? null"
              :alt="`Cover art for ${asRow(row).live_track_name ?? asRow(row).live_track_code}`"
              :width="40"
              :height="40"
              :preview-width="320"
              :fallback-src="UNKNOWN_ALBUM_ART_PATH"
              class="overflow-hidden rounded-md border bg-muted"
            />
          </div>
          <div
            v-else-if="column.id === 'code'"
            class="truncate font-mono text-xs"
          >
            {{ asRow(row).live_track_code }}
          </div>
          <div
            v-else-if="column.id === 'playlist_count'"
            class="text-xs tabular-nums"
          >
            <Tooltip v-if="asRow(row).playlist_count != null">
              <TooltipTrigger as-child>
                <span class="cursor-help">{{ asRow(row).playlist_count }}</span>
              </TooltipTrigger>
              <TooltipContent side="top" align="start">On Playlists</TooltipContent>
            </Tooltip>
            <span v-else class="text-muted-foreground">—</span>
          </div>
          <div
            v-else-if="column.id === 'rating'"
            class="flex items-center"
          >
            <TrackRatingBadge :rating="asRow(row).rating" size="sm" />
          </div>
          <div
            v-else-if="column.id === 'track_name'"
            class="truncate text-xs"
          >
            {{ asRow(row).live_track_name ?? 'Unknown' }}
          </div>
          <div
            v-else-if="column.id === 'artist_name'"
            class="truncate text-xs"
          >
            {{ asRow(row).live_track_artist_name ?? '—' }}
          </div>
          <div v-else-if="column.id === 'issue_type'" class="text-xs">
            <div class="flex items-center gap-1.5">
              <div class="truncate font-medium leading-snug">
                {{ asRow(row).issue_type_label ?? asRow(row).issue_type }}
              </div>
              <!-- One row per track: when a track has multiple matching issues the row's
                   severity/headline reflects the worst one, and this badge surfaces the
                   remaining count. The tooltip lists the other issue types so the count
                   is discoverable without expanding the row. Clicking still opens the
                   inspector for the track, which shows every issue in detail. -->
              <Tooltip v-if="(asRow(row).issues_count ?? 1) > 1">
                <TooltipTrigger as-child>
                  <span
                    role="button"
                    class="bg-muted text-muted-foreground hover:bg-muted/80 rounded px-1.5 py-0.5 text-[10px] font-medium leading-none tabular-nums shrink-0 cursor-pointer"
                    :aria-label="`${asRow(row).issues_count} issues on this track`"
                    @click.stop="onIssueRowClick(asRow(row))"
                  >
                    {{ asRow(row).issues_count }} issues
                  </span>
                </TooltipTrigger>
                <TooltipContent side="top" align="start" class="max-w-xs">
                  <div class="text-xs font-medium">Also on this track</div>
                  <ul class="mt-1 list-disc pl-4 text-xs">
                    <li v-for="label in otherIssueTypeLabels(asRow(row))" :key="label">
                      {{ label }}
                    </li>
                  </ul>
                </TooltipContent>
              </Tooltip>
            </div>
            <div class="text-muted-foreground font-mono text-[10px]">
              {{ asRow(row).issue_type }}
            </div>
          </div>
          <div
            v-else-if="column.id === 'severity'"
            class="flex items-center"
          >
            <SeverityBadge
              :severity="asRow(row).severity"
              :label="asRow(row).severity_label"
              size="sm"
            />
          </div>
          <div
            v-else-if="column.id === 'details'"
            class="truncate text-xs text-muted-foreground"
          >
            {{ detailsPreview(asRow(row).details) }}
          </div>
          <div
            v-else-if="column.id === 'verified_at'"
            class="text-xs"
          >
            <Tooltip>
              <TooltipTrigger as-child>
                <span class="whitespace-nowrap tabular-nums">{{
                  cellDateDisplay(asRow(row).verified_at)
                }}</span>
              </TooltipTrigger>
              <TooltipContent side="top">
                <TooltipRow :icon="BadgeCheck" label="Verified" class="tabular-nums">{{ cellDateDisplay(asRow(row).verified_at) }}</TooltipRow>
              </TooltipContent>
            </Tooltip>
          </div>
          <div
            v-else-if="column.id === 'created_at'"
            class="text-xs"
          >
            <Tooltip>
              <TooltipTrigger as-child>
                <span class="whitespace-nowrap tabular-nums">{{
                  cellDateDisplay(asRow(row).created_at)
                }}</span>
              </TooltipTrigger>
              <TooltipContent side="top">
                <TooltipRow :icon="CalendarPlus" label="Issue created" class="tabular-nums">{{ cellDateDisplay(asRow(row).created_at) }}</TooltipRow>
              </TooltipContent>
            </Tooltip>
          </div>
          <div
            v-else-if="column.id === 'uploaded_date'"
            class="text-xs"
          >
            <Tooltip>
              <TooltipTrigger as-child>
                <span class="whitespace-nowrap tabular-nums">{{
                  cellDateDisplay(asRow(row).uploaded_date)
                }}</span>
              </TooltipTrigger>
              <TooltipContent side="top">
                <TooltipRow :icon="Upload" label="File uploaded" class="tabular-nums">{{ cellDateDisplay(asRow(row).uploaded_date) }}</TooltipRow>
              </TooltipContent>
            </Tooltip>
          </div>
          <div
            v-else-if="column.id === 'track_uploaded_date'"
            class="text-xs"
          >
            <Tooltip>
              <TooltipTrigger as-child>
                <span class="whitespace-nowrap tabular-nums">{{
                  cellDateDisplay(asRow(row).track_uploaded_date)
                }}</span>
              </TooltipTrigger>
              <TooltipContent side="top">
                <TooltipRow :icon="Upload" label="Track uploaded" class="tabular-nums">{{ cellDateDisplay(asRow(row).track_uploaded_date) }}</TooltipRow>
              </TooltipContent>
            </Tooltip>
          </div>
          <div
            v-else-if="column.id === 'track_created_at'"
            class="text-xs"
          >
            <Tooltip>
              <TooltipTrigger as-child>
                <span class="whitespace-nowrap tabular-nums">{{
                  cellDateDisplay(asRow(row).track_created_at)
                }}</span>
              </TooltipTrigger>
              <TooltipContent side="top">
                <TooltipRow :icon="CalendarPlus" label="Track created" class="tabular-nums">{{ cellDateDisplay(asRow(row).track_created_at) }}</TooltipRow>
              </TooltipContent>
            </Tooltip>
          </div>
          <div
            v-else
            class="truncate text-xs"
          >
            {{ (asRow(row) as unknown as Record<string, unknown>)[column.key] ?? '—' }}
          </div>
        </template>
      </DataTable>
    </div>
  </div>
</template>
